import PasswordField from "../components/common/PasswordField";
import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { useNavigate } from "react-router-dom";
import AuthCardLayout from "../components/forms/AuthCardLayout";
import CountrySelect from "../components/common/CountrySelect";
import OtpStep from "../components/forms/OtpStep";
import AuthService from "../services/AuthService";
import { getErrorMessage } from "../services/apiUtils";
import LocaleContext from "../store/locale-context";
import { useTranslation } from "react-i18next";

interface RegisterFormValues {
  firstName: string;
  lastName: string;
  emailId: string;
  mobile: string;
  password: string;
  country: string;
}

const emptyForm: RegisterFormValues = {
  firstName: "",
  lastName: "",
  emailId: "",
  mobile: "",
  password: "",
  country: "PK",
};

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { country: topBarCountry } = React.useContext(LocaleContext);
  const [step, setStep] = React.useState<"details" | "otp">("details");

  const [values, setValues] = React.useState<RegisterFormValues>({
    ...emptyForm,
    country: topBarCountry || emptyForm.country,
  });
  const [otp, setOtp] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const setField = (name: keyof RegisterFormValues, value: string) =>
    setValues((prev) => ({ ...prev, [name]: value }));

  const handleRegister = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await AuthService.register({
        name: `${values.firstName} ${values.lastName}`.trim(),
        firstName: values.firstName,
        lastName: values.lastName,
        emailId: values.emailId,
        mobile: values.mobile,
        password: values.password,
        country: values.country,
      });
      await AuthService.sendOtp(values.emailId || values.mobile);
      setStep("otp");
    } catch (err) {
      setError(getErrorMessage(err, "Could not create your account. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await AuthService.verifyRegistrationOtp({
        identifier: values.emailId || values.mobile,
        otp,
      });
      navigate("/login");
    } catch (err) {
      setError(getErrorMessage(err, "Invalid or expired code. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === "otp") {
    return (
      <AuthCardLayout title={t("auth.verifyAccountTitle")}>
        <OtpStep
          identifier={values.emailId || values.mobile}
          otp={otp}
          onOtpChange={setOtp}
          onVerify={handleVerify}
          onResend={() => AuthService.resendOtp(values.emailId || values.mobile)}
          isVerifying={isSubmitting}
          error={error}
        />
      </AuthCardLayout>
    );
  }

  return (
    <AuthCardLayout title={t("auth.signUpTitle")}>
      <Box component="form" noValidate onSubmit={handleRegister} sx={{ width: "100%" }}>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          margin="normal"
          required
          fullWidth
          label={t("auth.firstName")}
          value={values.firstName}
          onChange={(e) => setField("firstName", e.target.value)}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          label={t("auth.lastName")}
          value={values.lastName}
          onChange={(e) => setField("lastName", e.target.value)}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          type="email"
          label={t("auth.email")}
          value={values.emailId}
          onChange={(e) => setField("emailId", e.target.value)}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          label={t("auth.mobileNumber")}
          value={values.mobile}
          onChange={(e) => setField("mobile", e.target.value)}
        />
        <PasswordField
          margin="normal"
          required
          fullWidth
          label={t("auth.password")}
          value={values.password}
          onChange={(e) => setField("password", e.target.value)}
        />
        <Box sx={{ mt: 2 }}>
          <CountrySelect
            label={t("auth.country")}
            value={values.country}
            onChange={(code) => setField("country", code)}
          />
        </Box>
        <Button type="submit" fullWidth variant="contained" disabled={isSubmitting} sx={{ mt: 3, mb: 2 }}>
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : t("auth.createAccount")}
        </Button>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Link component="button" type="button" variant="body2" onClick={() => navigate("/login")}>
            {t("auth.haveAccount")}
          </Link>
        </Box>
      </Box>
    </AuthCardLayout>
  );
};

export default RegisterPage;
